import api from "@/services/utils/apiInstance.ts";
import type {
  ApiError,
  CustomAxiosRequestConfig,
} from "@/types/services/utils/axios.type.ts";
import type { AxiosError } from "axios";
import { displayResponseError } from "@/utils/toastUtils.ts";
import { useAuthStore } from "@/stores/authStore.ts";
import {
  deobfuscatePayload,
  getObfuscationVersion,
  isObfuscationEnabled,
  isObfuscationEnvelope,
  obfuscatePayload,
} from "@/services/utils/obfuscation.ts";

const EXCLUDED_ROUTE = [
  "/api/nameplate/login",
  "/api/nameplate/refresh-token",
  "/api/nameplate/logout",
];

function getCookie(name: string) {
  const match = document.cookie.match(new RegExp("(^| )" + name + "=([^;]+)"));
  return match ? match[2] : null;
}

const setup = () => {
  const authStore = useAuthStore();

  api.interceptors.request.use((config) => {
    const csrf = getCookie("csrf_token");
    if (csrf) {
      config.headers["X-CSRF-Token"] = csrf;
    }

    if (isObfuscationEnabled()) {
      config.headers["X-Obfuscation-Accept"] = getObfuscationVersion();
    }

    if (
      isObfuscationEnabled() &&
      config.data !== undefined &&
      config.data !== null &&
      !(config.data instanceof FormData)
    ) {
      config.headers["X-Obfuscation"] = getObfuscationVersion();
      config.data = obfuscatePayload(config.data);
    }

    return config;
  });

  api.interceptors.response.use(
    (res) => {
      if (
        isObfuscationEnabled() &&
        res.headers?.["x-obfuscation"] === getObfuscationVersion() &&
        isObfuscationEnvelope(res.data)
      ) {
        res.data = deobfuscatePayload(res.data);
      }

      return res;
    },
    async (error) => {
      const { response } = error;
      const config = error.config as CustomAxiosRequestConfig;

      if (
        isObfuscationEnabled() &&
        response?.headers?.["x-obfuscation"] === getObfuscationVersion() &&
        isObfuscationEnvelope(response?.data)
      ) {
        response.data = deobfuscatePayload(response.data);
      }

      const errorKey =
        typeof response?.data === "string"
          ? response.data
          : (response?.data as ApiError)?.key;

      if (!EXCLUDED_ROUTE.some((route) => config.url?.includes(route))) {
        if (
          response?.status === 401 &&
          !config._retry &&
          (errorKey === "expired_token_error" ||
            errorKey === "invalid_token_error" ||
            errorKey === "invalid_jwt_error")
        ) {
          config._retry = true;
          try {
            await api.get("/api/nameplate/refresh-token");
            return api(config);
          } catch {
            await authStore.logout();
            displayResponseError(error as AxiosError<ApiError>);
            return Promise.reject(error);
          }
        }

        if (response?.status === 401) {
          await authStore.logout();
          displayResponseError(error as AxiosError<ApiError>);
          return Promise.reject(error);
        }

        if (response?.status === 403 && errorKey === "user_not_allowed_error") {
          await authStore.logout();
          displayResponseError(error as AxiosError<ApiError>);
          return Promise.reject(error);
        }
      }

      if (!config.disableGlobalError) {
        displayResponseError(error as AxiosError<ApiError>);
      }

      return Promise.reject(error);
    },
  );
};

export default setup;
